import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@styles/theme';
import { Button, Input, Card, CardContent, Spinner } from '@components/index';
import { AuthLayout } from '@layouts/index';
import { usersApi } from '@infrastructure/api';
const Form = styled.form `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;
const ErrorMessage = styled.div `
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.status.error}20;
  border-radius: ${theme.borderRadius.md};
`;
const SuccessMessage = styled.div `
  color: ${theme.colors.status.success};
  font-size: ${theme.typography.fontSize.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.status.success}20;
  border-radius: ${theme.borderRadius.md};
`;
const Title = styled.h2 `
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;
const Description = styled.p `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;
const SubmitButton = styled(Button) `
  margin-top: ${theme.spacing.sm};
`;
const LoadingContainer = styled.div `
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.xl};
`;
export function AcceptInvitationPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    useEffect(() => {
        // Validate token presence
        if (!token) {
            setError('Invalid invitation link. Please check your email for the correct link.');
            setIsValidating(false);
            return;
        }
        setIsValidating(false);
    }, [token]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        if (!token) {
            setError('Invalid invitation token');
            return;
        }
        setIsLoading(true);
        try {
            await usersApi.acceptInvitation({ token, name, password });
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to accept invitation';
            setError(message);
        }
        finally {
            setIsLoading(false);
        }
    };
    if (isValidating) {
        return (_jsx(AuthLayout, { children: _jsx(Card, { children: _jsx(CardContent, { children: _jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }) }) }) }));
    }
    if (success) {
        return (_jsx(AuthLayout, { children: _jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Title, { children: "Account Created" }), _jsx(SuccessMessage, { children: "Your account has been created successfully. Redirecting to login..." })] }) }) }));
    }
    return (_jsx(AuthLayout, { children: _jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Title, { children: "Accept Invitation" }), _jsx(Description, { children: "Complete your account setup to access the prototypes shared with you." }), _jsxs(Form, { onSubmit: handleSubmit, children: [error && _jsx(ErrorMessage, { children: error }), _jsx(Input, { label: "Full Name", type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "Enter your full name", required: true, autoComplete: "name" }), _jsx(Input, { label: "Password", type: "password", value: password, onChange: (e) => setPassword(e.target.value), placeholder: "Create a password", hint: "Must be at least 8 characters", required: true, autoComplete: "new-password" }), _jsx(Input, { label: "Confirm Password", type: "password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), placeholder: "Confirm your password", required: true, autoComplete: "new-password" }), _jsx(SubmitButton, { type: "submit", fullWidth: true, loading: isLoading, disabled: isLoading || !token, children: "Create Account" })] })] }) }) }));
}
